import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../api/client.js';
import clsx from 'clsx';

const TABS = [
  { id: 'overview',  label: 'Overview' },
  { id: 'pipeline',  label: 'Pipeline' },
];

function fmt$(v) {
  const n = Number(v || 0);
  if (n === 0) return '$0';
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + Math.round(n).toLocaleString();
  return '$' + n.toFixed(0);
}
function fmtPct(gp, sales) {
  const g = Number(gp || 0), s = Number(sales || 0);
  if (s === 0) return '—';
  return Math.round(100 * g / s) + '% margin';
}
function pct(curr, prev) {
  const a = Number(curr || 0), b = Number(prev || 0);
  if (b === 0) return null;
  return (a - b) / b;
}

export default function Executive() {
  const [tab, setTab] = useState('overview');

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Executive</div>
        <h1 className="text-2xl">Overview</h1>
        <div className="rule-crimson" />
      </header>

      <div className="border-b border-panelDark flex gap-1">
        {TABS.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-2 text-sm border-b-2 -mb-px',
              tab === t.id ? 'border-crimson text-crimson font-medium' : 'border-transparent text-navy hover:text-crimson'
            )}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview'  && <OverviewTab />}
      {tab === 'pipeline'  && <PipelineTab />}
    </div>
  );
}

function OverviewTab() {
  const q = useQuery({
    queryKey: ['executive', 'overview'],
    queryFn: () => api.get('/executive/overview').then(r => r.data),
    refetchInterval: 5 * 60_000,
  });
  if (q.isLoading) return <div className="text-muted">Loading…</div>;
  if (q.isError)   return <div className="text-crimson">Failed to load executive overview.</div>;
  const d = q.data;

  const partsMoM    = pct(d.revenue.parts.this_month,    d.revenue.parts.last_month);
  const partsYoY    = pct(d.revenue.parts.this_month,    d.revenue.parts.yoy_month);
  const serviceMoM  = pct(d.revenue.service.this_month,  d.revenue.service.last_month);
  const serviceYoY  = pct(d.revenue.service.this_month,  d.revenue.service.yoy_month);
  const trailerMoM  = pct(d.revenue.trailer?.this_month, d.revenue.trailer?.last_month);
  const trailerYoY  = pct(d.revenue.trailer?.this_month, d.revenue.trailer?.yoy_month);

  const staleTotal  = d.stale_aging.reduce((s, r) => s + Number(r.amt), 0);
  const staleStale  = d.stale_aging.find(r => r.hold_reason === 'stale_estimate') || { amt: 0, count: 0 };
  const staleParts  = d.stale_aging.find(r => r.hold_reason === 'waiting_parts') || { amt: 0, count: 0 };
  const staleInv    = d.stale_aging.find(r => r.hold_reason === 'ready_to_invoice') || { amt: 0, count: 0 };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted">Refreshes every 5 minutes. Cards link to the dashboard you'd drill into.</p>

      {/* Top KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
        <KpiCard
          label="Parts MTD"
          value={fmt$(d.revenue.parts.this_month)}
          subA={`Last mo: ${fmt$(d.revenue.parts.last_month)}`}
          subB={d.revenue.parts.gp != null ? `GP: ${fmt$(d.revenue.parts.gp)} · ${fmtPct(d.revenue.parts.gp, d.revenue.parts.this_month)}` : `YoY: ${fmt$(d.revenue.parts.yoy_month)}`}
          chip={pctChip(partsYoY, 'YoY')}
          to="/parts" />
        <KpiCard
          label="Service MTD"
          value={fmt$(d.revenue.service.this_month)}
          subA={`Last mo: ${fmt$(d.revenue.service.last_month)}`}
          subB={`YoY: ${fmt$(d.revenue.service.yoy_month)}`}
          chip={pctChip(serviceYoY, 'YoY')}
          to="/service" />
        <KpiCard
          label="Trailer MTD"
          value={fmt$(d.revenue.trailer?.this_month)}
          subA={`Last mo: ${fmt$(d.revenue.trailer?.last_month)}`}
          subB={d.revenue.trailer?.gp != null ? `GP: ${fmt$(d.revenue.trailer.gp)} · ${fmtPct(d.revenue.trailer.gp, d.revenue.trailer.this_month)}` : `YoY: ${fmt$(d.revenue.trailer?.yoy_month)}`}
          chip={pctChip(trailerYoY, 'YoY')}
          to="/trailer-sales" />
        <KpiCard
          label="Open pipeline"
          value={fmt$(d.pipeline.open_value)}
          subA={`${d.pipeline.open_count} opportunities`}
          subB={`Weighted: ${fmt$(d.pipeline.weighted)}`}
          to="/pipeline" />
        <KpiCard
          label="Comeback rate · 90d"
          value={`${d.comeback_rate.pct ?? '—'}%`}
          subA={`${d.comeback_rate.comebacks} of ${d.comeback_rate.total} invoiced`}
          to="/service"
          tone={Number(d.comeback_rate.pct) > 1 ? 'warn' : 'normal'}
        />
      </div>

      {/* Stale aging summary */}
      <section className="bg-amber-50 border border-amber-200 rounded p-3">
        <div className="flex items-baseline justify-between mb-2">
          <Link to="/aging" className="text-sm font-semibold text-amber-900 hover:underline">
            Aging repair-orders ({fmt$(staleTotal)} total)
          </Link>
          <span className="text-xs text-amber-800">click to drill in</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          <BucketChip label="Stale estimates" amt={staleStale.amt} count={staleStale.count} hint="Quoted, not authorized" />
          <BucketChip label="Waiting on parts" amt={staleParts.amt} count={staleParts.count} hint="Backordered" />
          <BucketChip label="Ready to invoice" amt={staleInv.amt} count={staleInv.count} hint="Slipped through cracks" />
        </div>
      </section>

      {/* At-risk customers */}
      <div className="grid md:grid-cols-2 gap-3">
        <RiskList
          title="Top lapsed parts customers"
          rows={d.at_risk.lapsed_parts}
          drillTo="/parts" />
        <RiskList
          title="Top service fade-out customers"
          rows={d.at_risk.service_fade_out}
          drillTo="/service" />
      </div>

      {/* Branch MTD breakdown */}
      {d.branch_revenue?.length > 1 && (
        <section className="bg-white border border-panelDark rounded">
          <header className="px-3 py-2 border-b border-panel">
            <div className="text-sm font-semibold text-navy">Branch revenue · MTD</div>
          </header>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-panel text-xs text-muted uppercase tracking-wide">
                <th className="px-3 py-1.5 text-left">Branch</th>
                <th className="px-3 py-1.5 text-right">Parts</th>
                <th className="px-3 py-1.5 text-right">Service</th>
                <th className="px-3 py-1.5 text-right">Trailer</th>
                <th className="px-3 py-1.5 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {d.branch_revenue.map(r => {
                const total = Number(r.parts) + Number(r.service) + Number(r.trailer);
                return (
                  <tr key={r.id} className="border-t border-panel first:border-t-0">
                    <td className="px-3 py-1.5 font-medium">{r.name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.parts)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.service)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.trailer)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmt$(total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* Rep performance */}
      {d.rep_performance?.length > 0 && (
        <section className="bg-white border border-panelDark rounded">
          <header className="px-3 py-2 border-b border-panel flex justify-between">
            <Link to="/rep-activity" className="text-sm font-semibold text-navy hover:underline">Rep performance</Link>
            <span className="text-xs text-muted">pipeline · conversion 90d · activity 30d</span>
          </header>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-panel text-xs text-muted uppercase tracking-wide">
                <th className="px-3 py-1.5 text-left">Rep</th>
                <th className="px-3 py-1.5 text-right">Pipeline</th>
                <th className="px-3 py-1.5 text-right">Opps</th>
                <th className="px-3 py-1.5 text-right">Won/Lost</th>
                <th className="px-3 py-1.5 text-right">Notes 30d</th>
              </tr>
            </thead>
            <tbody>
              {d.rep_performance.map(r => {
                const total90 = r.won_90d + r.lost_90d;
                const convPct = total90 > 0 ? Math.round(100 * r.won_90d / total90) : null;
                return (
                  <tr key={r.user_id} className="border-t border-panel first:border-t-0">
                    <td className="px-3 py-1.5 font-medium truncate max-w-[140px]">{r.full_name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.pipeline_value)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.open_count}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {total90 > 0
                        ? <span className={convPct >= 50 ? 'text-emerald-700' : 'text-rose-700'}>{convPct}% ({r.won_90d}/{total90})</span>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.notes_30d}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* New customers + aging fleet */}
      <div className="grid md:grid-cols-2 gap-3">
        <section className="bg-white border border-panelDark rounded">
          <header className="px-3 py-2 border-b border-panel flex justify-between">
            <div className="text-sm font-semibold text-navy">New customers (last 90 days)</div>
            <span className="text-xs text-muted">{d.new_customers.length}</span>
          </header>
          <table className="w-full text-sm">
            <tbody>
              {d.new_customers.map(r => (
                <tr key={r.customer_id} className="border-t border-panel first:border-t-0">
                  <td className="px-3 py-1.5">
                    <Link to={`/customers/${r.customer_id}`} className="text-navy hover:text-crimson">{r.name}</Link>
                    <div className="text-[11px] text-muted">first seen {r.first_period?.slice(0,10)}</div>
                  </td>
                  <td className="px-3 py-1.5 text-right text-xs">
                    P {fmt$(r.parts_total)} · S {fmt$(r.service_total)}
                  </td>
                </tr>
              ))}
              {d.new_customers.length === 0 && (
                <tr><td className="text-center text-muted py-4 text-sm">No new customers in window.</td></tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="bg-white border border-panelDark rounded">
          <header className="px-3 py-2 border-b border-panel flex justify-between">
            <Link to="/trailer-sales" className="text-sm font-semibold text-navy hover:underline">
              Aging fleet leaders
            </Link>
            <span className="text-xs text-muted">≥10y old</span>
          </header>
          <table className="w-full text-sm">
            <tbody>
              {d.aging_fleet.map(r => {
                const key = r.customer_id ?? `cus:${(r.cus_ids || []).join(',')}`;
                const branches = (r.excede_branch_ids || []).join(', ') || '—';
                const NameCell = r.customer_id
                  ? <Link to={`/customers/${r.customer_id}`} className="text-navy hover:text-crimson">{r.name}</Link>
                  : <span className="text-navy">{r.name}</span>;
                return (
                  <tr key={key} className="border-t border-panel first:border-t-0">
                    <td className="px-3 py-1.5 truncate">
                      {NameCell}
                      <div className="text-[11px] text-muted">br {branches} · fleet {r.fleet_size}</div>
                    </td>
                    <td className="px-3 py-1.5 text-right text-xs">
                      <span className="font-semibold tabular-nums">{r.over_10y}</span> over 10y
                    </td>
                  </tr>
                );
              })}
              {d.aging_fleet.length === 0 && (
                <tr><td className="text-center text-muted py-4 text-sm">No aging fleets identified.</td></tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

const DOMAIN_LABEL = { parts: 'Parts', service: 'Service', trailer_sales: 'Trailer', other: 'Other' };

function PipelineTab() {
  const q = useQuery({
    queryKey: ['opportunities', 'open'],
    queryFn: () => api.get('/opportunities', { params: { status: 'open' } }).then(r => r.data.data),
  });
  const rows = q.data || [];

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted">All open opportunities across all domains.</p>
      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Domain</th>
              <th className="text-left px-3 py-2">Stage</th>
              <th className="text-right px-3 py-2">Value</th>
              <th className="text-right px-3 py-2">Activity</th>
              <th className="text-right px-3 py-2">Close by</th>
              <th className="text-left px-3 py-2">Owner</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(o => (
              <tr key={o.id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  <Link to={`/pipeline/${o.id}`} className="text-navy hover:text-crimson font-medium">{o.name}</Link>
                </td>
                <td className="px-3 py-1.5">
                  {o.customer_id
                    ? <Link to={`/customers/${o.customer_id}`} className="text-navy hover:text-crimson">{o.customer_name}</Link>
                    : '—'}
                </td>
                <td className="px-3 py-1.5 text-xs text-muted">{DOMAIN_LABEL[o.domain] || o.domain || '—'}</td>
                <td className="px-3 py-1.5 capitalize">{o.stage}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(o.value_usd)}</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">
                  {o.source_note_date ? new Date(o.source_note_date).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{o.expected_close_date || '—'}</td>
                <td className="px-3 py-1.5 text-xs text-muted">{o.user_name || '—'}</td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted py-6">No open opportunities.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function pctChip(p, label) {
  if (p == null) return null;
  const sign = p >= 0 ? '+' : '';
  const tone = p < 0 ? 'rose' : 'emerald';
  return (
    <span className={clsx(
      'text-[11px] px-1.5 py-0.5 rounded font-medium',
      tone === 'rose' ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'
    )}>
      {sign}{(p * 100).toFixed(1)}% {label}
    </span>
  );
}

function KpiCard({ label, value, subA, subB, chip, to, tone }) {
  const Wrap = to ? Link : 'div';
  return (
    <Wrap to={to}
      className={clsx(
        'block border rounded px-3 py-2',
        tone === 'warn' ? 'bg-rose-50 border-rose-200' : 'bg-panel border-panelDark',
        to && 'hover:ring-1 hover:ring-crimson'
      )}>
      <div className="flex items-baseline justify-between">
        <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
        {chip}
      </div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      {subA && <div className="text-[11px] text-muted">{subA}</div>}
      {subB && <div className="text-[11px] text-muted">{subB}</div>}
    </Wrap>
  );
}

function BucketChip({ label, amt, count, hint }) {
  return (
    <div className="bg-white border border-amber-200 rounded p-2">
      <div className="text-xs font-medium text-amber-900">{label}</div>
      <div className="flex items-baseline justify-between mt-0.5">
        <span className="text-lg font-semibold tabular-nums">{fmt$(amt)}</span>
        <span className="text-[11px] text-muted">{count} SOs</span>
      </div>
      <div className="text-[11px] text-muted">{hint}</div>
    </div>
  );
}

function RiskList({ title, rows, drillTo }) {
  return (
    <section className="bg-white border border-panelDark rounded">
      <header className="px-3 py-2 border-b border-panel flex justify-between">
        <Link to={drillTo} className="text-sm font-semibold text-navy hover:underline">{title}</Link>
        <span className="text-xs text-muted">top 5 by score</span>
      </header>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(r => (
            <tr key={r.customer_id} className="border-t border-panel first:border-t-0">
              <td className="px-3 py-1.5 truncate">
                <Link to={`/customers/${r.customer_id}`} className="text-navy hover:text-crimson">{r.name}</Link>
                <div className="text-[11px] text-muted">silent {r.months_silent}mo · baseline {fmt$(r.baseline_avg)}/mo</div>
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmt$(r.at_risk_score)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td className="text-center text-muted py-4 text-sm">None matching.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
